import {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { defer } from 'es-toolkit/compat';
import { generateClientToken } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import { API } from 'api';
import { useMembershipState } from 'state/useMembershipState';
import { useIapState } from 'features/payments/hooks/useIapState';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { useMembershipManagement } from 'hooks/useMembershipManagement';
import { getDeviceInfo } from 'state/localStorageTyped';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import {
  MEMBERSHIP_PLANS,
  MembershipPlan,
} from 'features/membership/membership.constants';
import {
  getBecomeAMemberLink,
  getContinueOnWeb,
} from './MembershipPlanProvider.utils';

type MembershipPlanContextType = {
  continueOnWeb: boolean;
  verifyingToken: boolean;
  verificationError: Error | null;
  getNextLink: (plan: MembershipPlan) => string;
  handleNext: (plan: MembershipPlan) => void;
};

type MembershipPlanProviderProps = {
  children: ReactNode;
};

const MEMBERSHIP_PLAN_TO_ANALYTIC_EVENT = {
  [MEMBERSHIP_PLANS.yearly]: ANALYTICS_EVENTS.CLICKED.MEMBERSHIP_REGULAR,
  [MEMBERSHIP_PLANS.proYearly]: ANALYTICS_EVENTS.CLICKED.MEMBERSHIP_PRO_YEARLY,
  [MEMBERSHIP_PLANS.proMonthly]:
    ANALYTICS_EVENTS.CLICKED.MEMBERSHIP_PRO_MONTHLY,
} as const;

const useMembershipTokenRef = (): MutableRefObject<string> => {
  const tokenRef = useRef<string>('');
  if (!tokenRef.current) {
    tokenRef.current = generateClientToken();
  }
  return tokenRef;
};

const useSaveVerificationToken = (props: {
  isSupporter: boolean;
  userId?: number;
  verificationToken: string;
}): { verifyingToken: boolean; verificationError: Error | null } => {
  const { isSupporter, userId, verificationToken } = props;
  const { t } = useTranslation();
  const {
    mutate: saveVerificationToken,
    isPending: verifyingToken,
    error: verificationError,
  } = useMutation({
    mutationFn: async (id: number) => {
      await API.post('recurly_integration/one_time_donation_token/', {
        userId: id,
        verificationToken,
      });
    },
    onError: (error) => {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    },
  });

  useEffect(() => {
    if (isSupporter && userId) {
      saveVerificationToken(userId);
    }
  }, [saveVerificationToken, isSupporter, userId]);

  return {
    verifyingToken,
    verificationError,
  };
};

const MembershipPlanContext = createContext<
  MembershipPlanContextType | undefined
>(undefined);

export const MembershipPlanProvider = (
  props: MembershipPlanProviderProps,
): JSX.Element => {
  const { children } = props;
  const { user, isSupporter } = useAuthState();
  const { updateMembershipState } = useMembershipState();
  const { setSelectedProductId, setCheckoutDetails } = useIapState();
  const { isIosSubscription } = useMembershipManagement();
  const history = useHistory();
  const deviceInfo = getDeviceInfo();

  // This token is used as either a verification token or a client token.
  const tokenRef = useMembershipTokenRef();
  const osPlatform = Capacitor.getPlatform();

  const isIOS = osPlatform === 'ios';

  const isMembershipUpgrade = user?.supporterStatus?.planType === 'regular';

  const continueOnWeb = getContinueOnWeb({
    isMembershipUpgrade,
    isIosSubscription,
  });

  const userId = user?.id;
  const { verifyingToken, verificationError } = useSaveVerificationToken({
    isSupporter,
    userId,
    verificationToken: tokenRef.current,
  });

  const osSystem = deviceInfo?.operatingSystem;

  const updatePlan = useCallback(
    (plan: MembershipPlan) => {
      logEvent({ name: MEMBERSHIP_PLAN_TO_ANALYTIC_EVENT[plan] });
      if (isIOS) {
        setSelectedProductId(plan);
        return;
      }
      updateMembershipState({ plan });
    },
    [isIOS, setSelectedProductId, updateMembershipState],
  );

  const handleNext = useCallback(
    (plan: MembershipPlan): void => {
      if (continueOnWeb) {
        if (!isMembershipUpgrade || !isIosSubscription || osSystem !== 'ios') {
          // Go back to home map page when continue on web, but not for ios subscription upgrade on an ios os system
          defer(() => history.push('/'));
        }
        return;
      }

      updatePlan(plan);

      if (isIOS) {
        if (!isMembershipUpgrade) return;
        setCheckoutDetails({ verificationToken: tokenRef.current });
        return;
      }

      if (user && isSupporter) {
        updateMembershipState({
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          existingUser: true,
          verificationToken: tokenRef.current,
          userId: user.id,
        });
      } else {
        updateMembershipState({
          clientToken: tokenRef.current,
          existingUser: false,
        });
      }
    },
    [
      continueOnWeb,
      updatePlan,
      isIOS,
      user,
      isSupporter,
      isMembershipUpgrade,
      isIosSubscription,
      osSystem,
      history,
      setCheckoutDetails,
      tokenRef,
      updateMembershipState,
    ],
  );

  const getNextLink = useCallback(
    (plan: MembershipPlan): string =>
      getBecomeAMemberLink({
        clientToken: tokenRef.current,
        isSupporter,
        user,
        plan,
        isMembershipUpgrade,
        isIosSubscription,
      }),
    [isIosSubscription, isMembershipUpgrade, isSupporter, tokenRef, user],
  );

  const value = useMemo(
    () => ({
      continueOnWeb,
      verifyingToken,
      verificationError,
      getNextLink,
      handleNext,
    }),
    [continueOnWeb, verifyingToken, verificationError, getNextLink, handleNext],
  );

  return (
    <MembershipPlanContext.Provider value={value}>
      {children}
    </MembershipPlanContext.Provider>
  );
};

export const useMembershipPlan = (): MembershipPlanContextType => {
  const context = useContext(MembershipPlanContext);
  if (context === undefined) {
    throw new Error(
      'useMembershipPlan must be used within a MembershipPlanProvider',
    );
  }
  return context;
};
